import { Button, Dropdown, Icon } from '@wyz/ui';
import { BlobHelpers } from '@wyz/utils';
import { Link } from 'react-router';
import { CUSTOMER_LEVEL } from '@wyz/types';
import { FEATURES, ROLES } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { useApplicationContext, useConfig, Trans } from '~/common/context';
import { TranslationKeys } from '~/common/types';

type ExportOption = {
  i18key: keyof TranslationKeys;
  id: string;
  onClick: () => void;
  cond: boolean;
};

type ImportOption = {
  i18key: keyof TranslationKeys;
  id: string;
  to: string;
  cond: boolean;
};

export default function ExportImportAction() {
  const { apiUrl, tenantCode } = useConfig();
  const { auth } = useApplicationContext();
  const acl = useAccessControl();
  const isRedistributionEnabled = acl.isEnabledFeatures([
    FEATURES.REDISTRIBUTION,
  ]);

  const exportOptions: Record<string, ExportOption> = {
    [ROLES.ROLE_EXPORT_REDISTRIBUTION_CUSTOMERS]: {
      i18key: 'app.entity.list.actions.export_redistribution_data',
      id: 'export-redistribution',
      cond: isRedistributionEnabled,
      onClick: () => {
        BlobHelpers.download({
          url: `${apiUrl}/tenants/api/wtb/tenants/${tenantCode}/customers/export`,
          qs: {
            levels: [CUSTOMER_LEVEL.N2],
          },
          token: auth.user?.access_token ?? '',
        }).catch(() => {});
      },
    },
    [ROLES.ROLE_EXPORT_REDISTRIBUTION_CUSTOMERS_MODEL]: {
      i18key: 'app.entity.list.actions.export_redistribution_template',
      cond: isRedistributionEnabled,
      id: 'export-redistribution-template',
      onClick: () => {
        BlobHelpers.download({
          url: `${apiUrl}/tenants/api/wtb/tenants/${tenantCode}/customers/export/model`,
          qs: {
            levels: [CUSTOMER_LEVEL.N2],
          },
          token: auth.user?.access_token ?? '',
        }).catch(() => {});
      },
    },
    [ROLES.ROLE_EXPORT_DISTRIBUTION_CUSTOMERS]: {
      i18key: 'app.entity.list.actions.export_distribution_data',
      cond: true,
      id: 'export-distribution-customers',
      onClick: () => {
        BlobHelpers.download({
          url: `${apiUrl}/tenants/api/wtb/tenants/${tenantCode}/customers/export`,
          qs: {
            levels: [CUSTOMER_LEVEL.N1],
          },
          token: auth.user?.access_token ?? '',
        }).catch(() => {});
      },
    },
    [ROLES.ROLE_EXPORT_DISTRIBUTION_CUSTOMERS_MODEL]: {
      i18key: 'app.entity.list.actions.export_distribution_template',
      id: 'export-distribution-template',
      cond: true,
      onClick: () => {
        BlobHelpers.download({
          url: `${apiUrl}/tenants/api/wtb/tenants/${tenantCode}/customers/export/model`,
          token: auth.user?.access_token ?? '',
        }).catch(() => {});
      },
    },
    [ROLES.ROLE_EXPORT_PROXIMITY_STOCKS_CUSTOMERS]: {
      i18key: 'app.entity.list.actions.export_proximity_stocks_data',
      id: 'export-proximity-stocks-data',
      cond: true,
      onClick: () => {
        BlobHelpers.download({
          url: `${apiUrl}/tenants/api/wtb/tenants/${tenantCode}/customers/export/proximity-stocks`,
          qs: {
            levels: [CUSTOMER_LEVEL.N1],
          },
          token: auth.user?.access_token ?? '',
        }).catch(() => {});
      },
    },
    [ROLES.ROLE_EXPORT_PROXIMITY_STOCKS_CUSTOMERS_MODEL]: {
      i18key: 'app.entity.list.actions.export_proximity_stocks_template',
      id: 'export-proximity-stocks-template',
      cond: true,
      onClick: () => {
        BlobHelpers.download({
          url: `${apiUrl}/tenants/api/wtb/tenants/${tenantCode}/customers/export/proximity-stocks/model`,
          token: auth.user?.access_token ?? '',
        }).catch(() => {});
      },
    },
  };

  const importOptions: Record<string, ImportOption> = {
    [ROLES.ROLE_IMPORT_DISTRIBUTION_CUSTOMERS]: {
      i18key: 'app.entity.list.actions.import_distribution',
      id: 'import-distribution',
      to: 'import-distribution',
      cond: true,
    },
    [ROLES.ROLE_IMPORT_REDISTRIBUTION_CUSTOMERS]: {
      i18key: 'app.entity.list.actions.import_redistribution',
      id: 'import-redistribution',
      to: 'import-redistribution',
      cond: isRedistributionEnabled,
    },
    [ROLES.ROLE_IMPORT_PROXIMITY_STOCKS_CUSTOMERS_JOB]: {
      i18key: 'app.entity.list.actions.import_proximity_stocks',
      id: 'import-proximity-stocks',
      to: 'import-proximity-stocks',
      cond: true,
    },
  };

  const noImports = !acl.isGranted(Object.keys(importOptions), 'unanimous');

  if (noImports) {
    const enabledExportPermissions = Object.keys(exportOptions).filter(
      (permission) => acl.isGranted([permission]),
    );

    if (enabledExportPermissions.length === 1) {
      const [enabledExportPermission] = enabledExportPermissions;
      return (
        <Button
          id='import-export-actions-btn'
          onClick={exportOptions[enabledExportPermission].onClick}
          variant='primary'
        >
          <Icon icon='download' />
          <span>
            {' '}
            <Trans i18nKey='app.entity.list.actions.export' />
          </span>
        </Button>
      );
    } else {
      return (
        <Dropdown.Container>
          <Dropdown.Toggle
            bsPrefix='toggle'
            as='div'
            id='import-export-actions'
          >
            <Button
              id='import-export-actions-btn'
              onClick={() => {}}
              variant='primary'
            >
              <Icon icon='download' />
              <span>
                {' '}
                <Trans i18nKey='app.entity.list.actions.export' />
              </span>
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className='dropdown-caret dropdown-menu-card dropdown-menu-end'>
            {enabledExportPermissions.map((permission) => {
              const { cond, onClick, id, i18key } = exportOptions[permission];
              return (
                cond && (
                  <Dropdown.Item onClick={onClick} id={id}>
                    <Trans i18nKey={i18key} />
                  </Dropdown.Item>
                )
              );
            })}
          </Dropdown.Menu>
        </Dropdown.Container>
      );
    }
  }

  return (
    <Dropdown.Container>
      <Dropdown.Toggle bsPrefix='toggle' as='div' id='import-export-actions'>
        <Button
          id='import-export-actions-btn'
          onClick={() => {}}
          variant='primary'
        >
          <Icon icon='arrow-down' />
          <span>
            {' '}
            <Trans i18nKey='app.entity.list.actions.import_export' />
          </span>
        </Button>
      </Dropdown.Toggle>
      <Dropdown.Menu className='dropdown-caret dropdown-menu-card dropdown-menu-end'>
        {Object.entries(importOptions).map(([permission, info]) => {
          if (info.cond && acl.isGranted([permission], 'affirmative')) {
            return (
              <Dropdown.Item as={Link} to={info.to} id={info.id} key={info.id}>
                <Trans i18nKey={info.i18key} />
              </Dropdown.Item>
            );
          }
        })}
        {Object.entries(exportOptions).map(([permission, info]) => {
          const { id, cond, onClick, i18key } = info;
          if (cond && acl.isGranted([permission], 'affirmative')) {
            return (
              <Dropdown.Item onClick={onClick} id={id} key={id}>
                <Trans i18nKey={i18key} />
              </Dropdown.Item>
            );
          }
        })}
      </Dropdown.Menu>
    </Dropdown.Container>
  );
}
